import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";
import { Game } from "../game.js";
import { Screen } from "../screen_manager/screen.js";
import { ScreenManager, Screens } from "../screen_manager/screen_manager.js";
import { SceneManager } from "../core/scene_manager.js";
import { Renderer } from "../core/renderer.js";
import { CameraController } from "../core/camera_controller.js";
import { Cannon } from "../entities/cannon.js";
import { CannonBall } from "../entities/cannon_ball.js";
import { PhysicsTable } from "../entities/physics_table.js";
import { Ground } from "../entities/ground.js";
import { PhysicsCube } from "../entities/physics_cube.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { Vfx3DRuntime } from "../vfx_3d/vfx_3d_runtime.js";
import { GraphicsApply } from "../graphics/graphics_apply.js";
import { GraphicsProfile, GraphicsQuality, GraphicsSettings } from "../graphics/graphics_quality.js";
import { GlobalSettings } from "../global_settings.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { DialogManager } from "../ui/dialog/dialog_manager.js";
import { OverlayManager } from "../ui/overlay/overlay_manager.js";
import { OverlayFactory } from "../ui/overlay/overlay_factory.js";
import { OverlayId } from "../ui/overlay/overlay_ids.js";
import type { ContinueOverlayAnchor, ContinueOverlayParams } from "../ui/overlay/overlays/continue_overlay.js";
import { ImgID } from "../image_loader/img_id.js";
import { PrizeShotPrizesTooltip } from "../ui/screens/prize_shot_screen/prize_shot_prizes_panel.js";
import type { OfferDto } from "../api/dto/offers.dto.js";
import type { PrizeShotRewardsOverlayParams } from "../ui/overlay/overlays/prize_shot_rewards_overlay.js";
import { PrizeShotUI } from "../ui/screens/prize_shot_screen/prize_shot_ui.js";
import { PrizeShotAssetLoader, type PrizeShotAssets } from "./prize_shot_assets.js";
import { isMemoryConstrained } from "../utils/memory_policy.js";
import { PrizeShotHitLimiter } from "./prize_shot_hit_limiter.js";
import { PrizeShotShelf } from "./prize_shot_shelf.js";
import { PrizeShotContinueResult, PrizeShotService } from "./prize_shot_service.js";
import { PRIZE_SHOT_CELLS, type PrizeShotReward } from "./prize_shot_rules.js";

export const PRIZE_SHOT_CONTINUE_PLACEMENT = "prize_shot_continue";

/** Shown under Continue until the admin puts a real offer on the placement; it buys nothing. */
const PRIZE_SHOT_CONTINUE_OFFER_PREVIEW = {
  id: "prize_shot_continue_preview",
  presentation: "placement_dialog",
  rewards: { level_shots: 10, big_balls: 1, coins: 1000 },
} as unknown as OfferDto;

enum PrizeShotPhase
{
  Playing,
  /** No balls left on entry: the "More balls" button offers more. */
  Waiting,
  /** The batch is over, the rewards come after a short pause. */
  Ending,
  /** The rewards or the Continue overlay is up. */
  Deciding,
  Done,
}

/** The Prize Shot round: the prize shelf on our table. Runs as the GAME screen (opts.prize_shot). */
export class PrizeShotScreen extends Screen
{
  /** Same as GameScreen.TABLE_SCREEN_Y. */
  private static readonly TABLE_SCREEN_Y = 0.75;
  /** Holds the ball's shader program for the session, as GameScreen.warmup_materials does. */
  private static ball_warmup_material: THREE.Material | null = null;
  /** Longest the scene stays undrawn while prepareFirstDraw works (Game caps holds at 2.5 s). */
  private static readonly PREPARE_HOLD_MS = 2200;
  /** Past this the shader warmup draw compiles whatever is still compiling. */
  private static readonly COMPILE_WAIT_MS = 1500;
  private ball_probe: THREE.Mesh | null = null;

  private camera_controller!: CameraController;
  private assets: PrizeShotAssets | null = null;
  private cannon: Cannon | null = null;
  private table: PhysicsTable | null = null;
  private shelf: PrizeShotShelf | null = null;
  /** Replaced by a new board, hidden, disposed after the next warmup. */
  private readonly retired_shelves: PrizeShotShelf[] = [];
  private ground: Ground | null = null;
  private sun: THREE.DirectionalLight | null = null;
  private readonly shelf_bottom_center = new THREE.Vector3();
  private shelf_width = 0;
  private aim_body_handles = new Set<number>();

  private readonly hit_limiter = new PrizeShotHitLimiter<CannonBall>();
  private readonly ball_prev = new WeakMap<CannonBall, THREE.Vector3>();
  private readonly ball_velocity = new WeakMap<CannonBall, THREE.Vector3>();
  // OPT: Reused scratch vectors keep the per-frame projectile hot path allocation-free.
  private readonly ball_position_scratch = new THREE.Vector3();
  private readonly ball_velocity_scratch = new THREE.Vector3();
  /** The player's balls in flight and where each was aimed. */
  private readonly in_flight = new Map<CannonBall, THREE.Vector3>();
  private cascade: number[] = [];
  private cascade_timer = 0;

  private round_id = "";
  private phase = PrizeShotPhase.Playing;
  private shown_shots = -1;
  private since_last_shot = Infinity;
  private end_timer = 0;
  private extra_end_delay = 0;
  private aiming = false;
  /** A tap began while waiting for balls: on release it asks for more. */
  private more_tap = false;
  private warmup_pending = false;
  private ready = false;
  private destroyed = false;
  private blocked_tap_hit_test: ( ( client_x: number, client_y: number ) => boolean ) | null = null;

  public onShotsLeftChanged: ( shots_left: number ) => void = () => {};
  public onPopped: ( cell: number, reward: PrizeShotReward | null ) => void = () => {};
  public onRoundEndShown: ( shown: boolean ) => void = () => {};
  public onInvalidShot: ( event: PointerEvent ) => void = () => {};
  /** Client px of where the ball missed. */
  public onMiss: ( client_x: number, client_y: number ) => void = () => {};

  public nameUI(): Screens
  {
    return Screens.GAME;
  }

  public getShotsLeft(): number
  {
    return PrizeShotService.shotsLeft();
  }

  public setBlockedTapHitTest( fn: ( client_x: number, client_y: number ) => boolean ): void
  {
    this.blocked_tap_hit_test = fn;
  }

  public async init(): Promise<void>
  {
    this.round_id = PrizeShotService.round()?.round_id ?? "";

    try
    {
      this.assets = await PrizeShotAssetLoader.load();
    }
    catch ( e )
    {
      console.warn( "[PrizeShot] assets failed to load, back to the menu", e );
      if ( !this.destroyed )
        void ScreenManager.showScreen( Screens.MENU );
      return;
    }

    if ( this.destroyed )
      return;

    Vfx3DRuntime.registerAll();
    this.camera_controller = new CameraController();
    SceneManager.camera = this.camera_controller.getCamera();

    this.createLights();
    this.createWorld();
    this.createCannon();

    window.addEventListener( "pointerdown", this.onPointerDown );
    window.addEventListener( "pointermove", this.onPointerMove );
    window.addEventListener( "pointerup", this.onPointerUp );

    GraphicsApply.applyScene( SceneManager.world, GraphicsSettings.profile() );
    await this.prepareFirstDraw();
    if ( this.destroyed )
      return;
    this.warmupFirstUse();
    // A loader frame between the shader warmup and building the overlays, so
    // neither freezes it for the sum of both.
    await new Promise<void>( resolve => requestAnimationFrame( () => resolve() ) );
    if ( this.destroyed )
      return;
    this.prewarmOverlays();
    this.syncShots();
    void audioController.playSound( AudioClips.START_LEVEL );

    this.resumeSavedRound();
    this.ready = true;
  }

  /**
   * Fetches and decodes the event's pictures while the lobby is idle, off the
   * main thread, so the first entry does not decode them behind a frozen
   * loader. The models are parsed on entry: GLB parsing is main-thread work
   * (~90 ms on the phone) that would stall the lobby. Not on low-memory
   * devices: the decoded circus pictures are ~40 MB.
   */
  public static prefetchAssets(): Promise<void>
  {
    if ( !PrizeShotService.isActive() || isMemoryConstrained() )
      return Promise.resolve();

    return PrizeShotAssetLoader.prefetchPictures();
  }

  /**
   * Before the round is seen, and off the main thread where the device allows
   * it: the shader programs compile in parallel (compileAsync, through
   * KHR_parallel_shader_compile) and the scene's textures go to the GPU one
   * per frame. On the phone all of it was one 383 ms frame under the loader:
   * ~10 programs at 6-12 ms each and the 2048 px VFX atlas at 91 ms. The game
   * loop does not draw the scene meanwhile, or its first frame would do it all.
   * warmupFirstUse then only finishes what compileAsync does not cover (the
   * shadow pass).
   */
  private async prepareFirstDraw(): Promise<void>
  {
    const game = Game.instance;
    const renderer = Renderer.renderer;
    const previous_hold = game.sceneRevealHoldUntil();
    game.holdSceneRevealUntil( Math.max( previous_hold, performance.now() + PrizeShotScreen.PREPARE_HOLD_MS ) );

    try
    {
      this.addBallProbe();
      const restore = this.forceAllDrawable();
      let compiled: Promise<unknown>;
      try
      {
        compiled = renderer.compileAsync( SceneManager.scene, SceneManager.camera );
      }
      finally
      {
        restore();
      }

      for ( const texture of this.sceneTextures() )
      {
        if ( this.destroyed )
          return;
        if ( PrizeShotScreen.isUploaded( texture ) )
          continue;
        renderer.initTexture( texture );
        await PrizeShotScreen.nextFrame();
      }

      await Promise.race( [
        compiled,
        new Promise( resolve => setTimeout( resolve, PrizeShotScreen.COMPILE_WAIT_MS ) ),
      ] );
    }
    finally
    {
      game.holdSceneRevealUntil( previous_hold );
    }
  }

  /** Every object drawable for a warmup pass, the hidden and the empty batches too; returns the undo. */
  private forceAllDrawable(): () => void
  {
    const forced: { object: THREE.Object3D; visible: boolean; culled: boolean }[] = [];
    SceneManager.scene.traverse( object =>
    {
      forced.push( { object, visible: object.visible, culled: object.frustumCulled } );
      object.visible = true;
      object.frustumCulled = false;
    } );

    return () =>
    {
      for ( const { object, visible, culled } of forced )
      {
        object.visible = visible;
        object.frustumCulled = culled;
      }
    };
  }

  /** The ball appears only with the first shot: a stand-in gets its programs ready. */
  private addBallProbe(): void
  {
    if ( this.ball_probe )
      return;

    const probe = CannonBall.createWarmupProbe();
    probe.position.copy( this.shelf!.cellCenter( 0 ) );
    SceneManager.world.add( probe );
    this.ball_probe = probe;
    // Removing the probe must not free the ball's program before the first shot.
    if ( PrizeShotScreen.ball_warmup_material )
      ( probe.material as THREE.Material ).dispose();
    else
      PrizeShotScreen.ball_warmup_material = probe.material as THREE.Material;
  }

  private removeBallProbe(): void
  {
    if ( !this.ball_probe )
      return;
    SceneManager.world.remove( this.ball_probe );
    this.ball_probe.geometry.dispose();
    this.ball_probe = null;
  }

  /** Every texture the scene's materials sample, and what a pop may show. */
  private sceneTextures(): THREE.Texture[]
  {
    const textures = new Set<THREE.Texture>();
    const add = ( value: unknown ): void =>
    {
      const texture = value as THREE.Texture | null;
      if ( texture?.isTexture && !( texture as THREE.Texture & { isRenderTargetTexture?: boolean } ).isRenderTargetTexture )
        textures.add( texture );
    };

    add( SceneManager.scene.background );
    SceneManager.scene.traverse( object =>
    {
      const material = ( object as THREE.Mesh ).material;
      if ( !material )
        return;
      for ( const m of Array.isArray( material ) ? material : [ material ] )
      {
        for ( const value of Object.values( m ) )
          add( value );
        const uniforms = ( m as THREE.ShaderMaterial ).uniforms;
        if ( uniforms )
        {
          for ( const uniform of Object.values( uniforms ) )
            add( uniform?.value );
        }
      }
    } );
    for ( const texture of this.shelf?.warmupTextures() ?? [] )
      textures.add( texture );

    return [ ...textures ];
  }

  private static isUploaded( texture: THREE.Texture ): boolean
  {
    const props = Renderer.renderer.properties.get( texture ) as { __version?: number };
    return texture.version === 0 || props.__version === texture.version;
  }

  private static nextFrame(): Promise<void>
  {
    return new Promise( resolve => requestAnimationFrame( () => resolve() ) );
  }

  public update( dt: number ): void
  {
    // init awaits a frame after building the world; the round starts when it ends.
    if ( !this.shelf || !this.cannon || !this.ready )
      return;

    if ( this.warmup_pending )
    {
      this.warmup_pending = false;
      this.warmupFirstUse();
    }

    this.processBalls();
    this.updateCascade( dt );
    this.cannon.update( dt );
    this.shelf.update( dt );

    if ( !GlobalSettings.DEBUG_DISABLE_VFX3D )
      Vfx3DRuntime.update( dt );

    if ( this.cannon.hasActiveBalls() || this.cannon.hasPoseChanged() )
      Renderer.requestShadowUpdate();

    this.since_last_shot += dt;

    // The period ended here: the lobby hands over what it left.
    if ( this.phase !== PrizeShotPhase.Done && PrizeShotService.round()?.round_id !== this.round_id )
    {
      this.leaveToMenu();
      return;
    }

    this.syncShots();
    this.updatePhase( dt );
  }

  /** Back button and the settings menu: the round keeps its state for the next visit. */
  public onExit(): void
  {
    if ( this.phase === PrizeShotPhase.Deciding )
      return;

    void ScreenManager.showScreen( Screens.MENU );
  }

  public isWaitingForBalls(): boolean
  {
    return this.phase === PrizeShotPhase.Waiting && PrizeShotService.canBuyMore();
  }

  public requestMoreBalls(): void
  {
    if ( !this.isWaitingForBalls() || this.isUiBusy() )
      return;

    this.openContinue();
  }

  public destroy(): void
  {
    this.destroyed = true;

    GraphicsSettings.unsubscribe( this.graphics_listener );
    window.removeEventListener( "pointerdown", this.onPointerDown );
    window.removeEventListener( "pointermove", this.onPointerMove );
    window.removeEventListener( "pointerup", this.onPointerUp );
    PhysicsWorld.setPreStepHook( null );

    this.cannon?.dispose();
    this.cannon = null;
    this.shelf?.dispose();
    this.shelf = null;
    for ( const shelf of this.retired_shelves.splice( 0 ) )
      shelf.dispose();
    this.table?.dispose();
    this.table = null;
    this.ground?.dispose();
    this.ground = null;
    this.sun?.dispose();
    this.sun = null;
    this.aim_body_handles.clear();

    Vfx3DRuntime.reset();
    SceneManager.world.clear();
  }

  private createLights(): void
  {
    const sun = new THREE.DirectionalLight( 0xfff1e0, 4.2 );
    sun.position.set( 1.5, 5, 4 );
    sun.castShadow = true;
    sun.shadow.intensity = 0.45;
    sun.shadow.mapSize.set( 1024, 1024 );
    sun.shadow.camera.left = -8;
    sun.shadow.camera.right = 8;
    sun.shadow.camera.top = 10;
    sun.shadow.camera.bottom = -6;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 40;
    sun.shadow.bias = -0.0002;
    sun.shadow.normalBias = 0.012;
    sun.shadow.radius = 2;
    this.sun = sun;

    // Warm spill from the circus lights behind.
    const rim = new THREE.DirectionalLight( 0xffb36b, 1.2 );
    rim.position.set( -5, 3, -4 );

    const hemi = new THREE.HemisphereLight( 0xfff0e0, 0x4a1d3a, 0.45 );
    const ambient = new THREE.AmbientLight( 0x888888, 0.55 );

    SceneManager.world.add( sun, rim, hemi, ambient );
    GraphicsSettings.subscribe( this.graphics_listener );
  }

  private readonly graphics_listener = ( _quality: GraphicsQuality, profile: GraphicsProfile ): void =>
  {
    CannonBall.cast_shadow = profile.ball_shadow && profile.shadows !== "off";
    GraphicsApply.applyScene( SceneManager.world, profile );
    Renderer.requestShadowUpdate();
  };

  private createWorld(): void
  {
    this.shelf_width = PhysicsCube.getSize().x * 6;

    const backdrop_anchor = new THREE.Vector3( 0, -0.4, -4.25 );
    const table_position = this.camera_controller.floorPointAt( PrizeShotScreen.TABLE_SCREEN_Y, backdrop_anchor.y )
      ?? backdrop_anchor.clone();

    this.createGround( backdrop_anchor );
    this.createTable( table_position );
    this.createShelfFromRound();

    const frame_points = this.shelfCorners();
    this.fitCamera( table_position, frame_points );
    this.followCameraWithGround( frame_points );

    PhysicsWorld.setPreStepHook( () =>
    {
      for ( const ball of this.cannon?.getBalls() ?? [] )
        ball.capturePrePhysicsVelocity();
    } );
  }

  /** The circus is laid out like a level's field: the star where the dirt circle is. */
  private createGround( anchor: THREE.Vector3 ): void
  {
    const assets = this.assets!;

    this.ground = new Ground( anchor, false, PrizeShotScreen.TABLE_SCREEN_Y, {
      bottom: assets.floor,
      top: assets.stage,
      sky: assets.tent,
    } );
  }

  private createTable( position: THREE.Vector3 ): void
  {
    this.table = new PhysicsTable(
      position,
      this.shelf_width * 0.42,
      this.shelf_width * 1.12,
      ResourceID.TableBlueBaseMap
    );

    const top = this.table.getTopCenter();
    this.shelf_bottom_center.set( top.x, this.table.getTopSurfaceY(), top.z );
  }

  /** The shelf as the round left it: popped cells open, claimed prizes gone. */
  private createShelfFromRound(): void
  {
    const round = PrizeShotService.round();
    const popped = new Map<number, PrizeShotReward | null>();
    const claimed = new Set<number>();

    if ( round )
    {
      for ( const cell of round.popped )
        popped.set( cell, round.board[cell] );
      for ( const cell of round.popped.slice( 0, round.claimed_count ) )
        claimed.add( cell );
    }

    this.buildShelf( popped, claimed );
  }

  private buildShelf( popped: ReadonlyMap<number, PrizeShotReward | null> = new Map(), claimed: ReadonlySet<number> = new Set() ): void
  {
    // A program lives while a material that was drawn with it lives. The new
    // shelf's materials are not drawn yet, so the old shelf is only hidden here
    // and disposed after warmupFirstUse; disposed now, it freed four programs
    // and the next frame compiled them again (122 ms at CPU x4).
    if ( this.shelf )
    {
      this.shelf.hide();
      this.retired_shelves.push( this.shelf );
    }
    this.shelf = new PrizeShotShelf( this.assets!, this.shelf_bottom_center, this.shelf_width, popped, claimed );
    this.shelf.prepareRewards( PrizeShotService.round()?.board ?? [] );
    this.aim_body_handles = new Set( [ ...this.table!.getBodyHandles(), this.shelf.getBodyHandle() ] );
  }

  /**
   * Shader programs are compiled and textures uploaded the first time they are
   * drawn. The ball, its muzzle fire, a prize, its glow and the burst shards
   * only appear mid-round, so that cost used to land on the first shot and the
   * first prize as 50-100 ms frames (CPU x4). Here everything in the scene,
   * the hidden and the empty batches too, is drawn once into one pixel, with
   * the shadow pass, before the round is seen.
   */
  private warmupFirstUse(): void
  {
    const renderer = Renderer.renderer;
    const camera = SceneManager.camera;
    this.addBallProbe();

    for ( const texture of this.shelf!.warmupTextures() )
      renderer.initTexture( texture );

    const restore = this.forceAllDrawable();
    try
    {
      renderer.setScissor( 0, 0, 1, 1 );
      renderer.setScissorTest( true );
      // three draws the shadow pass before it sets up the frame's lights, and a
      // depth material keeps the program of its first use. Without a pass that
      // sets up the lights first, the very first shadow pass compiles the depth
      // programs for zero lights, and every new board compiles them again.
      renderer.shadowMap.needsUpdate = false;
      Renderer.render( SceneManager.scene, camera );
      Renderer.requestShadowUpdate();
      Renderer.render( SceneManager.scene, camera );
    }
    finally
    {
      renderer.setScissorTest( false );
      restore();
      this.removeBallProbe();
      Renderer.requestShadowUpdate();
    }

    for ( const shelf of this.retired_shelves.splice( 0 ) )
      shelf.dispose();
  }

  private shelfCorners(): THREE.Vector3[]
  {
    const box = this.shelf!.getBox();
    const corners: THREE.Vector3[] = [];

    for ( const x of [ box.min.x, box.max.x ] )
      for ( const y of [ box.min.y, box.max.y ] )
        for ( const z of [ box.min.z, box.max.z ] )
          corners.push( new THREE.Vector3( x, y, z ) );

    return corners;
  }

  /** On short and wide screens the camera backs off until the shelf clears the logo and the timer. */
  private fitCamera( table_position: THREE.Vector3, frame_points: THREE.Vector3[] ): void
  {
    const bounds = Renderer.getCanvasBounds();
    const hud_bottom = bounds.height > 0 ? PrizeShotUI.hudBottom() / bounds.height : 0;
    const cannon_pivot = Cannon.composedPivot();

    this.camera_controller.fitAlong( table_position, frame_points, {
      frame_fill: 0.74,
      min_depth: this.camera_controller.composedDepth( cannon_pivot ) + 10,
      keep_above: { point: cannon_pivot, y: 0.5 },
      max_y: 1 - 2 * hud_bottom,
    } );
  }

  private followCameraWithGround( frame_points: THREE.Vector3[] ): void
  {
    const view = this.camera_controller.getCamera().matrixWorldInverse;
    const depths = frame_points.map( p => -p.clone().applyMatrix4( view ).z );

    this.ground!.followCamera( this.camera_controller.getHomeEye(), Math.max( 0, ...depths ) );
  }

  private createCannon(): void
  {
    const shelf = this.shelf!;
    const aim_plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      new THREE.Vector3( 0, 0, 1 ),
      new THREE.Vector3( 0, 0, shelf.getFrontZ() )
    );
    const forward = this.camera_controller.getCamera()
      .getWorldDirection( new THREE.Vector3() )
      .setY( 0 )
      .normalize();

    this.cannon = new Cannon( aim_plane, collider => this.isAimTarget( collider ) );
    this.cannon.followView( point => this.camera_controller.fromComposedView( point ) );
    this.cannon.setFarLimit( forward, shelf.getBox().max.dot( forward ) + 6 );
    this.cannon.aimAtWorldPoint( shelf.cellCenter( 9 ) );
  }

  private isAimTarget( collider: RAPIER.Collider ): boolean
  {
    const body = collider.parent()?.handle;
    return body !== undefined && this.aim_body_handles.has( body );
  }

  /**
   * An earlier visit was cut short: a finished batch with prizes goes to the
   * claim, one without starts over or waits for more balls.
   */
  private resumeSavedRound(): void
  {
    if ( PrizeShotService.isBoardCleared() )
      this.startCascade( 0.2 );

    if ( !PrizeShotService.isBatchOver() )
      return;

    if ( PrizeShotService.unclaimedRewards().length > 0 || PrizeShotService.isBoardCleared() )
    {
      this.beginEnding( 0.4 );
      return;
    }

    PrizeShotService.claim();
    if ( this.getShotsLeft() === 0 )
      this.phase = PrizeShotPhase.Waiting;
  }

  private processBalls(): void
  {
    for ( const ball of this.cannon!.getBalls() )
    {
      if ( !ball.alive )
        continue;

      const p = ball.body.translation();
      const now = this.ball_position_scratch.set( p.x, p.y, p.z );
      let prev = this.ball_prev.get( ball );
      if ( !prev )
      {
        // One retained vector per fired ball replaces one temporary vector per frame.
        prev = now.clone();
        this.ball_prev.set( ball, prev );
      }

      const cell = this.hit_limiter.consume( ball, this.shelf!.hitTest( prev, now, ball.getRadius() ) );
      prev.copy( now );
      if ( cell === null )
        this.checkBounce( ball, now );
      else
        this.onBalloonHit( ball, cell );
    }

    // Gone without touching a balloon: a miss where it was aimed.
    for ( const [ ball, target ] of this.in_flight )
    {
      if ( ball.alive )
        continue;

      this.in_flight.delete( ball );
      if ( !this.hit_limiter.hasConsumed( ball ) )
        this.showMiss( target );
    }
  }

  private onBalloonHit( ball: CannonBall, cell: number ): void
  {
    // Removed at once: a stopped fading body could reach a neighbouring balloon next frame.
    ball.destroy();
    this.in_flight.delete( ball );

    const reward = PrizeShotService.pop( cell );
    if ( reward === undefined )
      return;

    this.shelf!.pop( cell, reward );
    void audioController.playSound( AudioClips.GLASS_CUBE_HIT );

    if ( reward )
      void audioController.playSound( AudioClips.COINS_GROUP_FLY, false, 0.5 );
    if ( reward?.grand )
      this.extra_end_delay = 0.5;
    if ( reward && PrizeShotService.prizesLeft() === 0 )
      this.startCascade( 0.35 );

    this.onPopped( cell, reward );
    Renderer.requestShadowUpdate();
  }

  /** A ball that bounced before any balloon is a miss: it pops nothing more and fades out. */
  private checkBounce( ball: CannonBall, position: THREE.Vector3 ): void
  {
    if ( !this.in_flight.has( ball ) )
      return;

    const v = ball.body.linvel();
    const velocity = this.ball_velocity_scratch.set( v.x, v.y, v.z );
    const before = this.ball_velocity.get( ball );

    if ( !before )
    {
      this.ball_velocity.set( ball, velocity.clone() );
      return;
    }

    const before_length_sq = before.lengthSq();
    const dot = velocity.dot( before );
    const speed_product = velocity.length() * Math.sqrt( before_length_sq );
    before.copy( velocity );

    if ( before_length_sq < 1 )
      return;
    // Turned by more than ~37° since the last frame: a bounce.
    if ( dot > 0.8 * speed_product )
      return;

    this.hit_limiter.markMissed( ball );
    this.in_flight.delete( ball );
    ball.forceFadeOut();
    this.showMiss( position );
  }

  private showMiss( world: THREE.Vector3 ): void
  {
    const ndc = world.clone().project( SceneManager.camera );
    if ( ndc.z > 1 )
      return;

    const bounds = Renderer.getCanvasBounds();
    const client_x = bounds.left + ( ndc.x * 0.5 + 0.5 ) * bounds.width;
    const client_y = bounds.top + ( -ndc.y * 0.5 + 0.5 ) * bounds.height;

    this.onMiss( client_x, client_y );
  }

  /** The last prize is out: the whole balloons left pop one by one. */
  private startCascade( delay_s: number ): void
  {
    const shelf = this.shelf;
    if ( !shelf )
      return;

    this.cascade = [];
    for ( let cell = 0; cell < PRIZE_SHOT_CELLS; cell++ )
    {
      if ( shelf.hasBalloon( cell ) )
        this.cascade.push( cell );
    }
    this.cascade_timer = delay_s;
  }

  private updateCascade( dt: number ): void
  {
    if ( this.cascade.length === 0 )
      return;

    this.cascade_timer -= dt;
    while ( this.cascade_timer <= 0 && this.cascade.length > 0 )
    {
      const cell = this.cascade.shift()!;
      this.cascade_timer += 0.08;

      if ( !this.shelf!.hasBalloon( cell ) )
        continue;

      this.shelf!.pop( cell, null );
      void audioController.playSound( AudioClips.GLASS_CUBE_HIT, false, 0.6 );
      Renderer.requestShadowUpdate();
    }
  }

  private updatePhase( dt: number ): void
  {
    if ( this.phase === PrizeShotPhase.Playing )
    {
      if ( PrizeShotService.isBatchOver() && this.areBallsSettled() )
      {
        this.beginEnding( 0.6 + this.extra_end_delay );
        this.extra_end_delay = 0;
      }
      return;
    }

    if ( this.phase !== PrizeShotPhase.Ending )
      return;

    this.end_timer -= dt;
    if ( this.end_timer <= 0 && this.cascade.length === 0 )
      this.openRoundEnd();
  }

  private areBallsSettled(): boolean
  {
    if ( this.cascade.length > 0 )
      return false;

    // The last ball gets this long to reach the shelf before the batch ends.
    return !this.cannon!.hasActiveBalls() || this.since_last_shot >= 2.2;
  }

  /** Balls given while waiting (the QA buttons) put the round back in play. */
  private syncShots(): void
  {
    const shots = this.getShotsLeft();
    if ( shots === this.shown_shots )
      return;

    this.shown_shots = shots;
    this.onShotsLeftChanged( shots );

    if ( this.phase === PrizeShotPhase.Waiting && shots > 0 )
      this.resume();
  }

  private beginEnding( delay_s: number ): void
  {
    this.phase = PrizeShotPhase.Ending;
    this.end_timer = delay_s;
    this.aiming = false;
  }

  /** What the batch found is taken first; a batch that found nothing with balls left just goes on. */
  private openRoundEnd(): void
  {
    if ( this.isUiBusy() )
      return;

    const rewards = PrizeShotService.unclaimedRewards();
    if ( rewards.length === 0 && this.getShotsLeft() > 0 && !PrizeShotService.isBoardCleared() )
    {
      PrizeShotService.claim();
      this.resume();
      return;
    }

    this.phase = PrizeShotPhase.Deciding;
    this.onRoundEndShown( true );
    this.openRewards( rewards );
  }

  private openRewards( rewards: PrizeShotReward[] ): void
  {
    const board_no = PrizeShotService.round()?.board_no ?? 0;

    OverlayFactory.openOverlay( OverlayId.PRIZE_SHOT_REWARDS_OVERLAY, {
      rewards,
      on_claim: () => PrizeShotService.claim(),
      on_closed: () => this.afterClaim( board_no ),
    } as PrizeShotRewardsOverlayParams );
  }

  private afterClaim( board_no: number ): void
  {
    if ( this.destroyed || this.phase === PrizeShotPhase.Done )
      return;

    const round = PrizeShotService.round();
    if ( !round || round.round_id !== this.round_id )
    {
      this.leaveToMenu();
      return;
    }

    // Every prize was taken: a fresh board was dealt.
    if ( round.board_no !== board_no )
    {
      this.cascade = [];
      this.buildShelf();
      // In the next game frame, right before its own render overwrites the
      // canvas: drawn from here, the one-pixel pass could reach the screen alone.
      this.warmup_pending = true;
    }
    else
    {
      this.shelf?.removeClaimedPrizes( new Set( round.popped.slice( 0, round.claimed_count ) ) );
    }
    Renderer.requestShadowUpdate();

    // Closed without "Take" (the back button): the rewards come back.
    if ( this.getShotsLeft() > 0 || PrizeShotService.unclaimedRewards().length > 0 )
      this.resume();
    else
      this.openContinue();
  }

  private openContinue(): void
  {
    this.phase = PrizeShotPhase.Deciding;
    this.onRoundEndShown( true );

    OverlayFactory.openOverlay( OverlayId.CONTINUE_OVERLAY, this.continueParams() );
  }

  private continueParams(): ContinueOverlayParams
  {
    const config = PrizeShotService.getConfig().continue;

    return {
      offer: { price: config.price, shots: config.shots },
      purchase_index: PrizeShotService.round()?.continues ?? 0,
      special_offer_placement: PRIZE_SHOT_CONTINUE_PLACEMENT,
      special_offer_description_key: "PRIZE_SHOT.OFFER_PLUS_BALLS",
      special_offer_preview: PRIZE_SHOT_CONTINUE_OFFER_PREVIEW,
      purchase: () => this.buyContinue(),
      info: {
        img_id: ImgID.UI_DailyTasksScreenInfo,
        // daily_info.png: a 110 px button in a 147 px square.
        fill: 110 / 147,
        tooltip: ( ctx: CanvasRenderingContext2D, anchor: ContinueOverlayAnchor ) =>
          PrizeShotPrizesTooltip.createAt( ctx, PrizeShotService.round()!, anchor, Game.getContentWidth() ),
      },
      onBuy: ( shots: number ) => this.onContinueBought( shots ),
      onCloseButton: () => this.declineContinue(),
      onClose: () =>
      {
        if ( this.phase !== PrizeShotPhase.Done )
          this.leaveToMenu();
      },
    };
  }

  /**
   * The rewards (one layout per number of card rows, and the empty one) and
   * Continue are built now, behind the loading screen, and warmed into the
   * overlay pool: built at the end of a batch, each cost two 50-60 ms frames
   * at CPU x4. Their params act on nothing; the real ones come on open.
   */
  private prewarmOverlays(): void
  {
    const round = PrizeShotService.round();
    const sample = round?.board.find( ( reward ): reward is PrizeShotReward => reward !== null );
    const rewards = ( count: number ): PrizeShotReward[] => sample ? new Array( count ).fill( sample ) : [];
    const rewards_params = ( count: number ): PrizeShotRewardsOverlayParams => ( { rewards: rewards( count ), on_claim: () => {} } );
    const prize_count = round?.board.filter( reward => reward !== null ).length ?? 0;

    const requests: Array<{ id: OverlayId; params: unknown }> = [
      { id: OverlayId.CONTINUE_OVERLAY, params: { ...this.continueParams(), onBuy: () => {}, onCloseButton: () => true, onClose: () => {}, prewarm: true } },
      { id: OverlayId.PRIZE_SHOT_REWARDS_OVERLAY, params: rewards_params( 0 ) },
    ];
    // Every count; the factory builds one overlay per layout.
    for ( let count = 1; sample && count <= prize_count; count++ )
      requests.push( { id: OverlayId.PRIZE_SHOT_REWARDS_OVERLAY, params: rewards_params( count ) } );

    void OverlayFactory.prewarmOverlays( requests );
  }

  /** The service adds the balls itself, so the dialog has none to hand over. */
  private async buyContinue(): Promise<number>
  {
    const result = await PrizeShotService.buyContinue();
    if ( result !== PrizeShotContinueResult.Ok )
      throw new Error( `prize shot continue: ${result}` );

    return 0;
  }

  /** Only the special offer under the dialog brings balls of its own. */
  private onContinueBought( shots: number ): void
  {
    if ( shots > 0 )
      PrizeShotService.addShots( shots );

    this.resume();
  }

  /** The menu comes up under the dialog first, so the board does not flash between them. */
  private declineContinue(): boolean
  {
    if ( this.phase === PrizeShotPhase.Done )
      return true;

    this.phase = PrizeShotPhase.Done;
    void ScreenManager.showScreen( Screens.MENU ).finally( () =>
    {
      if ( OverlayManager.isOverlayOpen( OverlayId.CONTINUE_OVERLAY ) )
        OverlayManager.closeOverlay();
    } );
    return true;
  }

  private leaveToMenu(): void
  {
    this.phase = PrizeShotPhase.Done;
    void ScreenManager.showScreen( Screens.MENU );
  }

  private resume(): void
  {
    this.phase = PrizeShotPhase.Playing;
    this.since_last_shot = Infinity;
    this.onRoundEndShown( false );
    this.syncShots();
  }

  private isUiBusy(): boolean
  {
    return Game.instance.isLoadingGameScreenShown()
      || DialogManager.isAnyDialogOpened()
      || OverlayManager.isAnyOverlayOpened();
  }

  private canAim(): boolean
  {
    return this.phase === PrizeShotPhase.Playing
      && !PrizeShotService.isBatchOver()
      && !!this.cannon
      && !this.isUiBusy();
  }

  private isBlockedTap( event: PointerEvent ): boolean
  {
    return this.blocked_tap_hit_test?.( event.clientX, event.clientY ) ?? false;
  }

  /** Only the shelf and the table top are worth a ball, not the legs or the floor. */
  private isOnTable( target: THREE.Vector3 | null ): boolean
  {
    // A target a little under the table top edge still counts as on it.
    return target !== null && target.y >= this.table!.getTopSurfaceY() - 0.05;
  }

  private fire( event: PointerEvent ): void
  {
    const cannon = this.cannon!;
    const before = cannon.getBalls().length;
    const target = cannon.fire( event );
    if ( target === null )
      return;

    const balls = cannon.getBalls();
    if ( balls.length > before )
      this.in_flight.set( balls[balls.length - 1], target );

    PrizeShotService.spendShot();
    this.since_last_shot = 0;
    void audioController.playSound( AudioClips.CANNON_SHOT );
    this.syncShots();
  }

  private readonly onPointerDown = ( event: PointerEvent ): void =>
  {
    this.more_tap = this.isWaitingForBalls() && !this.isBlockedTap( event );

    if ( !this.canAim() || this.getShotsLeft() === 0 || this.isBlockedTap( event ) )
      return;

    this.aiming = true;
    this.cannon!.aim( event );
  };

  private readonly onPointerMove = ( event: PointerEvent ): void =>
  {
    if ( !this.aiming || !this.canAim() )
      return;

    this.cannon!.aim( event );
  };

  private readonly onPointerUp = ( event: PointerEvent ): void =>
  {
    if ( this.more_tap )
    {
      this.more_tap = false;
      this.requestMoreBalls();
      return;
    }

    if ( !this.aiming )
      return;

    this.aiming = false;
    if ( !this.canAim() || this.getShotsLeft() === 0 )
      return;

    if ( !this.isOnTable( this.cannon!.getTarget( event ) ) )
    {
      this.onInvalidShot( event );
      void audioController.playSound( AudioClips.UI_CLICK );
      return;
    }

    this.fire( event );
  };
}
